/**
 * CustomInput.tsx
 *
 * The portal-styled text input extracted from example/mvp/login.html.
 * Matches the `.auth-input` styles in auth.css exactly.
 *
 * Supports:
 *   - Standard text / email / tel / password types
 *   - Show/hide password toggle (rendered automatically when type="password")
 *   - Error state (red border via .auth-input--error)
 *   - Label above the field
 *   - Optional helper / error message below the field
 *
 * All auth.css classes are applied here — screens just use <CustomInput>.
 *
 * Junior devs: do NOT add auth0 SDK logic here.  This is a pure UI component.
 * Forward the ref if you need imperative focus (e.g., OTP auto-advance).
 */

import { forwardRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
  error?: string;
  hint?: string;
  /** Override: show the show/hide toggle even when type != "password" */
  showToggle?: boolean;
}

// ---------------------------------------------------------------------------
// Eye SVGs
// ---------------------------------------------------------------------------

function EyeOpen() {
  return (
    <svg
      viewBox="0 0 32 32"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 16 C5.5 11 10 8 16 8 C22 8 26.5 11 29 16 C26.5 21 22 24 16 24 C10 24 5.5 21 3 16 Z" />
      <circle cx="16" cy="16" r="4" />
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg
      viewBox="0 0 32 32"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 16 C5.5 11 10 8 16 8 C22 8 26.5 11 29 16 C26.5 21 22 24 16 24 C10 24 5.5 21 3 16 Z" />
      <circle cx="16" cy="16" r="4" />
      <line x1="4" y1="4" x2="28" y2="28" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  function CustomInput(
    { label, id, error, hint, showToggle, type = "text", className, ...rest },
    ref
  ) {
    const [visible, setVisible] = useState(false);

    const isPassword = type === "password" || showToggle;
    const resolvedType = isPassword ? (visible ? "text" : "password") : type;

    const inputClass = [
      "auth-input",
      error ? "auth-input--error" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="auth-field">
        <label htmlFor={id} className="auth-label">
          {label}
        </label>

        {isPassword ? (
          <div className="auth-pw-wrap">
            <input
              ref={ref}
              id={id}
              type={resolvedType}
              className={inputClass}
              aria-describedby={error ? `${id}-error` : undefined}
              {...rest}
            />
            <button
              type="button"
              className="auth-pw-toggle"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide password" : "Show password"}
              aria-pressed={visible}
            >
              {visible ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
        ) : (
          <input
            ref={ref}
            id={id}
            type={type}
            className={inputClass}
            aria-describedby={error ? `${id}-error` : undefined}
            {...rest}
          />
        )}

        {error && (
          <p id={`${id}-error`} className="auth-field-error" role="alert">
            {error}
          </p>
        )}

        {!error && hint && <p className="auth-hint">{hint}</p>}
      </div>
    );
  }
);

export default CustomInput;
